import {
  Box,
  EmptyState,
  FieldSelectionList,
  FieldSelectionListProps,
  FlexBox,
  Icon,
  SelectionList,
  Spinner,
  Text,
  TextWithHighlight,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import Fuse from 'fuse.js';
import { Role } from 'lib-shared-user-role';
import groupBy from 'lodash/groupBy';
import { Fragment, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { Field } from 'redux-form';

import { rolesApi } from 'apis/roles';

import styles from './rolesField.module.scss';
import { useInitializeRolesFieldValue } from './utils/useInitializeRolesField';

type RolesFieldProps = {
  required: boolean;
  disabled: boolean;
  onInit: (roles: Role[]) => void;
};

export const RolesField = ({
  required,
  disabled,
  onInit,
}: RolesFieldProps): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  useInitializeRolesFieldValue(onInit);

  const { roles, isLoading } = rolesApi.useReadRolesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      roles: data?.data.roles ?? [],
    }),
  });

  const filteredRoles = useMemo(() => {
    const minMatchCharLength = 2;

    if (
      !debouncedSearchTerm ||
      debouncedSearchTerm.length < minMatchCharLength
    ) {
      return roles;
    }

    const fuse = new Fuse(roles, {
      keys: ['id', 'name', 'description'],
      threshold: 0.2,
      minMatchCharLength,
      distance: 200,
    });

    return fuse.search(debouncedSearchTerm).map(({ item }) => item);
  }, [debouncedSearchTerm, roles]);

  const groupedRoles = useMemo(
    () =>
      groupBy(filteredRoles, (role) => {
        return role.is_system_role ? 'System roles' : 'Custom roles';
      }),
    [filteredRoles],
  );

  return (
    // @ts-expect-error This generic type does not entirely satisfy Redux Form. But it's good enough
    // to just ensure that the props passed to the FieldSelectionList component are type-safe.
    <Field<FieldSelectionListProps<Role>>
      label="Role"
      name="custom_roles"
      placeholder="Select a role"
      disabled={disabled}
      required={required}
      onBlur={(event) => event.preventDefault()}
      filter={{
        value: searchTerm,
        onChange: (e) => setSearchTerm(e.target.value),
      }}
      equalityFn={(a, b) => a.id === b.id}
      getItemTitleByValue={(role) => role?.name}
      component={FieldSelectionList}
      dropdownProps={{ className: styles.dropdown }}
    >
      {Object.entries(groupedRoles).map(([group, roles], _, array) => (
        <Fragment key={group}>
          {array.length > 1 && <SelectionList.Divider label={group} />}

          {roles.map((role) => (
            <SelectionList.Item key={role.id} value={role}>
              <FlexBox gap="300" alignItems="center">
                <Tooltip
                  wrap="div"
                  wrapProps={{ style: { display: 'flex' } }}
                  content={role.is_system_role ? 'System role' : 'Custom role'}
                >
                  <Icon
                    name={role.is_system_role ? 'role-system' : 'role-custom'}
                    color={
                      role.is_system_role
                        ? typography.color.tertiary
                        : typography.color.primary
                    }
                    size={24}
                  />
                </Tooltip>

                <Box>
                  <Text.Paragraph
                    variant="1"
                    as="span"
                    subdued={role.is_system_role}
                  >
                    <TextWithHighlight
                      text={role.name}
                      highlight={debouncedSearchTerm}
                    />
                  </Text.Paragraph>

                  {!role.is_system_role && role.description && (
                    <Text.Paragraph subdued variant="2">
                      <TextWithHighlight
                        text={role.description}
                        highlight={debouncedSearchTerm}
                      />
                    </Text.Paragraph>
                  )}
                </Box>
              </FlexBox>
            </SelectionList.Item>
          ))}
        </Fragment>
      ))}

      {isLoading && (
        <FlexBox
          as="li"
          padding="250 300"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size={20} />
        </FlexBox>
      )}

      {!isLoading && filteredRoles.length === 0 && (
        <Box as="li" padding="250 300">
          <EmptyState
            title="No roles found"
            description="Try changing your search criteria."
          />
        </Box>
      )}
    </Field>
  );
};
